/** Date range and comparison pickers: the 15 presets, N for "last N", custom dates, include today. */

import type { ReactNode } from 'react';

import { COMPARE_LABELS, PRESET_LABELS } from './format.ts';
import type { CompareSetting, RangeSetting } from './reports-api.ts';

export function DateControl({
  range,
  onRange,
  compare,
  onCompare,
}: {
  range: RangeSetting;
  onRange: (range: RangeSetting) => void;
  compare?: CompareSetting | undefined;
  onCompare?: ((compare: CompareSetting) => void) | undefined;
}): ReactNode {
  const needsN = range.preset === 'LAST_N_DAYS' || range.preset === 'LAST_N_MONTHS';
  const rolling = ['LAST_7_DAYS', 'LAST_30_DAYS', 'LAST_N_DAYS'].includes(range.preset);
  return (
    <div className="rd">
      <select
        aria-label="Date range"
        value={range.preset}
        onChange={(event) =>
          onRange({
            preset: event.target.value,
            ...(event.target.value.includes('_N_') ? { n: range.n ?? 3 } : {}),
          })
        }
      >
        {Object.entries(PRESET_LABELS).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>
      {needsN && (
        <input
          type="number"
          className="rd-n"
          aria-label={range.preset === 'LAST_N_DAYS' ? 'Number of days' : 'Number of months'}
          min={1}
          max={730}
          value={range.n ?? 3}
          onChange={(event) =>
            onRange({ ...range, n: Math.max(1, Math.min(730, Number(event.target.value) || 1)) })
          }
        />
      )}
      {range.preset === 'CUSTOM' && (
        <>
          <input
            type="date"
            aria-label="From"
            value={range.from ?? ''}
            onChange={(event) => onRange({ ...range, from: event.target.value })}
          />
          <input
            type="date"
            aria-label="To"
            value={range.to ?? ''}
            onChange={(event) => onRange({ ...range, to: event.target.value })}
          />
        </>
      )}
      {rolling && (
        <label className="rd-today">
          <input
            type="checkbox"
            checked={Boolean(range.includeToday)}
            onChange={(event) => onRange({ ...range, includeToday: event.target.checked })}
          />
          Include today
        </label>
      )}
      {compare && onCompare && (
        <>
          <select
            aria-label="Compare to"
            value={compare.mode}
            onChange={(event) =>
              onCompare({
                mode: event.target.value,
                ...(compare.format ? { format: compare.format } : {}),
              })
            }
          >
            {Object.entries(COMPARE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          {compare.mode !== 'NONE' && (
            <select
              aria-label="Show change as"
              value={compare.format ?? 'PERCENT'}
              onChange={(event) =>
                onCompare({ ...compare, format: event.target.value as 'PERCENT' | 'ABSOLUTE' })
              }
            >
              <option value="PERCENT">Percentage change</option>
              <option value="ABSOLUTE">Absolute change</option>
            </select>
          )}
          {compare.mode === 'CUSTOM' && (
            <>
              <input
                type="date"
                aria-label="Compare from"
                value={compare.from ?? ''}
                onChange={(event) => onCompare({ ...compare, from: event.target.value })}
              />
              <input
                type="date"
                aria-label="Compare to date"
                value={compare.to ?? ''}
                onChange={(event) => onCompare({ ...compare, to: event.target.value })}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
